// Global ⌘K / Ctrl+K command palette — backed by our unified /api/search
// (Blog + Academy + Shop, all stored in MongoDB on Railway). Opens as a centered
// modal with keyboard navigation. No more WordPress/Tutor/Woo dependencies.
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ArrowRight,
  FileText,
  ShoppingBag,
  GraduationCap,
  Map as MapIcon,
  Wine,
  Sparkles,
  Command as CommandIcon,
  X,
} from "lucide-react";
import { useLang, useT } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";
import { searchStaticIndex } from "@/lib/staticSearchIndex";

// Default quick-links shown when the input is empty (curated discovery)
const QUICK_LINKS_BUILDER = (t) => [
  { id: "terroir-ai", label: t("nav.terroir_ai"), to: "/geovinum-ai", Icon: Sparkles },
  { id: "shop", label: t("nav.shop"), to: "/shop", Icon: ShoppingBag },
  { id: "academy", label: t("nav.academy"), to: "/academy", Icon: GraduationCap },
  { id: "atlas", label: t("nav.resources.atlas"), to: "/cartes", Icon: MapIcon },
  { id: "blog", label: t("nav.blog"), to: "/blog", Icon: FileText },
];

const ICON_BY_KIND = { shop: ShoppingBag, academy: GraduationCap, blog: FileText, atlas: MapIcon };

// Eyebrow label shown under each result — distinguishes appellation vs cepage atlases.
// `t()` returns the raw key when a translation is missing, which is
// TRUTHY — so the `|| "fallback"` pattern below never fires. We detect
// the raw-key case by testing `startsWith("cmd.")` and swap in a
// hand-tuned per-language fallback so the CommandBar never leaks
// "cmd.kind.glossary" strings to the user.
const FALLBACK = {
  fr: { glossary: "Glossaire", guide: "Guide", page: "Page", blog: "Article", shop: "Boutique", academy: "Academy", atlas_appellation: "Appellation", atlas_cepage: "Cépage" },
  en: { glossary: "Glossary", guide: "Guide", page: "Page", blog: "Article", shop: "Shop", academy: "Academy", atlas_appellation: "Appellation", atlas_cepage: "Grape variety" },
  es: { glossary: "Glosario", guide: "Guía", page: "Página", blog: "Artículo", shop: "Tienda", academy: "Academy", atlas_appellation: "Denominación", atlas_cepage: "Variedad" },
  pt: { glossary: "Glossário", guide: "Guia", page: "Página", blog: "Artigo", shop: "Loja", academy: "Academy", atlas_appellation: "Denominação", atlas_cepage: "Casta" },
};

const _label = (t, lang, subkey) => {
  const key = `cmd.kind.${subkey}`;
  const v = t(key);
  if (!v || v.startsWith("cmd.")) {
    return (FALLBACK[lang] || FALLBACK.fr)[subkey] || subkey;
  }
  return v;
};

const KIND_LABEL = (kind, item, t, lang = "fr") => {
  if (kind === "atlas") {
    if (item.atlas_kind === "cepage") return _label(t, lang, "atlas_cepage");
    return _label(t, lang, "atlas_appellation");
  }
  if (kind === "academy") return _label(t, lang, "academy");
  if (kind === "shop")    return _label(t, lang, "shop");
  if (kind === "blog")    return _label(t, lang, "blog");
  if (kind === "term")    return _label(t, lang, "glossary");
  if (kind === "pillar")  return _label(t, lang, "guide");
  if (kind === "page")    return _label(t, lang, "page");
  return _label(t, lang, "blog");
};

function highlight(text, q) {
  if (!q) return text;
  const i = (text || "").toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] rounded-[2px] px-0.5">
        {text.slice(i, i + q.length)}
      </mark>
      {text.slice(i + q.length)}
    </>
  );
}

const isMac = () =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || "");

export const CommandBar = ({ open, onClose }) => {
  const t = useT();
  const lang = useLang();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);

  // Reset state when closed + body scroll lock
  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
      setActiveIdx(0);
      return;
    }
    setTimeout(() => inputRef.current?.focus(), 30);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Debounced unified search against /api/search
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults([]); return; }
    const h = setTimeout(async () => {
      const { ok, data } = await apiFetch(`/api/search?q=${encodeURIComponent(q.trim())}&limit=12&language=${lang}`);
      const items = ok ? (data?.items || []) : [];
      setResults(items);
      // Zero-result signal → push to the leads queue so Audrey can
      // action them. Kept lightweight: fire-and-forget, ignore errors.
      // kind_intent="any" — the CommandBar has no filter context to
      // pre-classify, so we let Audrey triage from the admin panel.
      if (items.length === 0 && q.trim().length >= 3) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/producer-leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: q.trim(), source: "cmdk", lang, kind_intent: "any" }),
        }).catch(() => {});
      }
    }, 500);  // longer debounce than the 200ms search — only report after
              // the user has visibly finished typing
    return () => clearTimeout(h);
  }, [q, lang, open]);

  // Build flat list of items for keyboard navigation.
  // Merges local static index (pillars, glossary, static pages) with
  // backend API results so `⌘K → "kimméridgien"` surfaces both the
  // glossary entry AND any blog post that mentions it.
  const items = useMemo(() => {
    if (!q.trim()) {
      return QUICK_LINKS_BUILDER(t).map((l) => ({
        kind: "link",
        Icon: l.Icon,
        title: l.label,
        subtitle: l.to,
        action: () => navigate(l.to),
        testId: `cmdk-quick-${l.id}`,
      }));
    }
    // Local static hits come FIRST — they're instantaneous and cover
    // the app's own pages / glossary. Backend results follow.
    const staticHits = searchStaticIndex(q, 8).map((it) => ({
      kind: it.kind,
      Icon: it.Icon || Sparkles,
      title: it.title,
      subtitle: `${KIND_LABEL(it.kind, it, t, lang)} · ${it.excerpt}`,
      action: () => {
        // Hash navigation for glossary terms — react-router doesn't
        // auto-scroll on same-route hash change, so we set it manually.
        if (it.url.includes("#")) {
          navigate(it.url);
          const hash = it.url.split("#")[1];
          setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
        } else {
          navigate(it.url);
        }
      },
      testId: `cmdk-${it.kind}-${it.slug}`,
    }));
    const apiHits = results.map((it) => ({
      kind: it.kind,
      Icon: ICON_BY_KIND[it.kind] || Sparkles,
      image: it.image_url,
      title: it.title,
      subtitle: it.kind === "atlas" && it.appellation
        ? `${KIND_LABEL(it.kind, it, t, lang)} · ${it.appellation}`
        : `${KIND_LABEL(it.kind, it, t, lang)}${it.excerpt ? ` · ${it.excerpt}` : ""}`,
      action: () => navigate(it.url),
      testId: `cmdk-${it.kind}-${it.slug}`,
    }));
    // De-dupe by url so a page listed in both indexes doesn't repeat.
    const seen = new Set();
    return [...staticHits, ...apiHits].filter((it) => {
      const key = it.testId;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [q, results, t, navigate]);

  // Keyboard navigation
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(items.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const it = items[activeIdx];
        if (it) {
          it.action();
          onClose();
        } else if (q.trim()) {
          navigate(`/search?q=${encodeURIComponent(q.trim())}`);
          onClose();
        }
      }
    },
    [items, activeIdx, q, navigate, onClose]
  );

  useEffect(() => {
    setActiveIdx(0);
  }, [items.length]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4 bg-black/55 backdrop-blur-sm animate-in fade-in duration-150"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="cmdk-overlay"
    >
      <div
        className="w-full max-w-2xl bg-white rounded-[10px] shadow-[0_30px_80px_-20px_rgba(18,24,32,0.45)] overflow-hidden border border-[color:var(--gv-line)]"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[color:var(--gv-line)]">
          <Search size={18} className="text-[color:var(--gv-ink)]/45" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("nav.search_placeholder")}
            className="flex-1 bg-transparent outline-none text-[15px] text-[color:var(--gv-ink)] placeholder:text-[color:var(--gv-ink)]/40"
            data-testid="cmdk-input"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />
          <button
            onClick={onClose}
            className="text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/40 hover:text-[color:var(--gv-ink)] border border-[color:var(--gv-line)] rounded-[4px] px-1.5 py-0.5"
            data-testid="cmdk-close"
            aria-label="Close"
          >
            ESC
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2" onKeyDown={onKeyDown}>
          {!q.trim() && (
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.22em] text-[color:var(--gv-ink)]/45">
              {t("cmdk.quick_links") || "Quick links"}
            </div>
          )}
          {q.trim() && items.length === 0 && (
            <div className="p-8 text-center">
              <div className="text-[14px] text-[color:var(--gv-ink)]/70 mb-1">
                {t("common.no_results")}
              </div>
              <div className="text-[12.5px] text-[color:var(--gv-ink)]/45">
                {t("common.try_another")}
              </div>
            </div>
          )}
          <ul>
            {items.map((it, i) => {
              const active = i === activeIdx;
              return (
                <li key={`${it.testId}-${i}`}>
                  <button
                    onClick={() => {
                      it.action();
                      onClose();
                    }}
                    onMouseEnter={() => setActiveIdx(i)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[6px] text-left transition-colors ${
                      active ? "bg-[color:var(--gv-mist)]" : "hover:bg-[color:var(--gv-mist)]/60"
                    }`}
                    data-testid={it.testId}
                  >
                    {it.image ? (
                      <img
                        src={it.image}
                        alt=""
                        className="w-9 h-9 rounded-[5px] object-cover border border-[color:var(--gv-line)]"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-[5px] bg-white border border-[color:var(--gv-line)] flex items-center justify-center">
                        <it.Icon size={15} className="text-[color:var(--gv-blue)]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] text-[color:var(--gv-ink)] truncate">
                        {q ? highlight(it.title || "", q) : it.title}
                      </div>
                      {it.subtitle && (
                        <div className="text-[12px] text-[color:var(--gv-ink)]/55 truncate">
                          {it.subtitle}
                        </div>
                      )}
                    </div>
                    {active && <ArrowRight size={14} className="text-[color:var(--gv-pink)]" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex items-center justify-between px-4 py-2.5 border-t border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/40 text-[11px] text-[color:var(--gv-ink)]/55">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded-[3px] bg-white border border-[color:var(--gv-line)] text-[10px]">
                ↑
              </kbd>
              <kbd className="px-1.5 py-0.5 rounded-[3px] bg-white border border-[color:var(--gv-line)] text-[10px]">
                ↓
              </kbd>
              {t("cmdk.navigate") || "Navigate"}
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded-[3px] bg-white border border-[color:var(--gv-line)] text-[10px]">
                ↵
              </kbd>
              {t("cmdk.select") || "Select"}
            </span>
          </div>
          {q.trim() && (
            <button
              onClick={() => {
                navigate(`/search?q=${encodeURIComponent(q.trim())}`);
                onClose();
              }}
              className="text-[color:var(--gv-pink)] hover:underline"
              data-testid="cmdk-see-all"
            >
              {t("common.see_all")} «&nbsp;{q}&nbsp;»
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Trigger button — embeds the ⌘K hint and dispatches a global event
export const CommandBarTrigger = ({ className = "", testId = "cmdk-trigger" }) => {
  const t = useT();
  const [mac, setMac] = useState(false);
  useEffect(() => setMac(isMac()), []);
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new Event("geovinum:open-cmdk"))}
      className={`group inline-flex items-center gap-2 h-10 pl-3 pr-2 rounded-[5px] bg-[color:var(--gv-mist)] border border-transparent hover:border-[color:var(--gv-pink)]/40 hover:bg-white transition-all min-w-[220px] md:min-w-[280px] text-left ${className}`}
      data-testid={testId}
      aria-label="Open search"
    >
      <Search size={15} className="text-[color:var(--gv-ink)]/45 shrink-0" />
      <span className="flex-1 text-[13.5px] text-[color:var(--gv-ink)]/55 truncate">
        {t("nav.search_placeholder")}
      </span>
      <span className="hidden md:inline-flex items-center gap-1 shrink-0">
        <kbd className="text-[10px] font-mono text-[color:var(--gv-ink)]/55 bg-white border border-[color:var(--gv-line)] rounded-[3px] px-1.5 py-0.5">
          {mac ? "⌘" : "Ctrl"}
        </kbd>
        <kbd className="text-[10px] font-mono text-[color:var(--gv-ink)]/55 bg-white border border-[color:var(--gv-line)] rounded-[3px] px-1.5 py-0.5">
          K
        </kbd>
      </span>
    </button>
  );
};

// Global mount — modal + hotkey listener, lives in Layout so it works on
// mobile/tablet too (even when the navbar trigger is hidden).
export const GlobalCommandBar = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      const cmd = e.metaKey || e.ctrlKey;
      const tag = document.activeElement?.tagName;
      const isInput = tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable;
      if (cmd && k === "k") {
        e.preventDefault();
        setOpen(true);
      } else if (k === "/" && !isInput) {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onEvent = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("geovinum:open-cmdk", onEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("geovinum:open-cmdk", onEvent);
    };
  }, []);
  return <CommandBar open={open} onClose={() => setOpen(false)} />;
};

export default CommandBarTrigger;
